import React, { useState } from "react";
import { Pressable, Text, View } from "react-native";
import AIExhibitNarrationPressable from "../audio/AIExhibitNarrationPressable";
import { useAIExhibitFixedNarration } from "../audio/useAIExhibitFixedNarration";
import { AI_EXHIBIT_COLORS as C } from "../aiExhibitTheme";

const NARRATION_BY_REVIEW_ID = Object.freeze({
  clear_positive: "sentiment_loved_movie",
  mixed_but_negative: "sentiment_mixed_visuals_story",
  sarcastic_ambiguous: "sentiment_so_bad_entertaining",
  neutral_fine: "sentiment_it_was_fine",
});

function ProbabilityRow({ label, value }) {
  return (
    <View style={{ marginTop: 12 }}>
      <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
        <Text style={{ color: C.text, fontSize: 16, fontWeight: "800" }}>{label}</Text>
        <Text style={{ color: C.accent, fontSize: 16, fontWeight: "900" }}>{value}%</Text>
      </View>
      <View style={{ height: 11, marginTop: 6, borderRadius: 999, backgroundColor: C.surface, overflow: "hidden" }}>
        <View style={{ width: `${value}%`, height: "100%", borderRadius: 999, backgroundColor: C.accent }} />
      </View>
    </View>
  );
}

export default function SentimentClassificationScreen({
  copy,
  language = "en",
  audioEnabled = false,
}) {
  const { playNarration } = useAIExhibitFixedNarration({
    audioEnabled,
    language,
  });
  const [index, setIndex] = useState(0);
  const [guess, setGuess] = useState(null);
  const [revealed, setRevealed] = useState(false);

  const review = copy.sentiment.reviews[index];
  const labels = [copy.sentiment.positive, copy.sentiment.neutral, copy.sentiment.negative];

  const nextReview = () => {
    setIndex((current) => (current + 1) % copy.sentiment.reviews.length);
    setGuess(null);
    setRevealed(false);
  };

  return (
    <View style={{ padding: 28 }}>
      <Text style={{ color: C.accent, fontSize: 13, fontWeight: "900", letterSpacing: 1.2 }}>{copy.sentiment.eyebrow}</Text>
      <Text style={{ color: C.text, fontSize: 44, lineHeight: 50, fontWeight: "900", marginTop: 8 }}>{copy.sentiment.title}</Text>
      <Text style={{ color: C.muted, fontSize: 18, lineHeight: 27, marginTop: 8, maxWidth: 920 }}>{copy.sentiment.subtitle}</Text>

      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 18, marginTop: 26 }}>
        <View style={{ flex: 1, minWidth: 360, padding: 24, borderRadius: 20, borderWidth: 1, borderColor: C.border, backgroundColor: C.surfaceRaised }}>
          <Text style={{ color: C.muted, fontSize: 13, fontWeight: "800" }}>REVIEW {index + 1} / {copy.sentiment.reviews.length}</Text>
          <Text style={{ color: C.text, fontSize: 25, lineHeight: 36, fontWeight: "800", marginTop: 18 }}>“{review.text}”</Text>
          <Text style={{ color: C.muted, fontSize: 15, marginTop: 24 }}>Your classification:</Text>

          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10, marginTop: 10 }}>
            {labels.map((label) => {
              const active = guess === label;
              return (
                <Pressable
                  key={label}
                  onPress={() => { setGuess(label); setRevealed(false); }}
                  style={({ pressed }) => ({
                    flex: 1, minWidth: 135, minHeight: 60, borderRadius: 14, borderWidth: active ? 2 : 1,
                    borderColor: active ? C.accent : C.border, backgroundColor: active || pressed ? C.accentSoft : C.surface,
                    alignItems: "center", justifyContent: "center", paddingHorizontal: 12,
                  })}
                >
                  <Text style={{ color: C.text, fontSize: 17, fontWeight: "900" }}>{label}</Text>
                </Pressable>
              );
            })}
          </View>

          <Pressable
            disabled={!guess}
            onPress={() => {
              setRevealed(true);
              playNarration(NARRATION_BY_REVIEW_ID[review.id]);
            }}
            style={({ pressed }) => ({
              marginTop: 18, minHeight: 54, alignSelf: "flex-start", paddingHorizontal: 18, paddingVertical: 12,
              borderRadius: 14, borderWidth: 1, borderColor: C.accent,
              backgroundColor: pressed ? C.surface : C.accentSoft, opacity: guess ? 1 : 0.4,
            })}
          >
            <Text style={{ color: C.text, fontSize: 16, fontWeight: "900" }}>{copy.sentiment.reveal}</Text>
          </Pressable>
        </View>

        <View style={{ flex: 1, minWidth: 360, padding: 24, borderRadius: 20, borderWidth: 1, borderColor: C.border, backgroundColor: C.surfaceRaised }}>
          {!revealed ? (
            <View style={{ flex: 1, alignItems: "center", justifyContent: "center", minHeight: 260 }}>
              <Text style={{ color: C.muted, fontSize: 18, textAlign: "center" }}>Make your guess, then reveal the model's classification.</Text>
            </View>
          ) : (
            <>
              <Text style={{ color: C.text, fontSize: 20, fontWeight: "900" }}>Illustrative classifier output</Text>
              <ProbabilityRow label={copy.sentiment.positive} value={review.probabilities[0]} />
              <ProbabilityRow label={copy.sentiment.neutral} value={review.probabilities[1]} />
              <ProbabilityRow label={copy.sentiment.negative} value={review.probabilities[2]} />

              <View style={{ marginTop: 20, padding: 16, borderRadius: 14, borderLeftWidth: 5, borderWidth: 1, borderColor: C.border, borderLeftColor: C.accent, backgroundColor: C.accentSoft }}>
                <Text style={{ color: C.text, fontSize: 16, lineHeight: 25 }}>{review.explanation}</Text>
              </View>

              <AIExhibitNarrationPressable
                audioEnabled={audioEnabled}
                onPress={() => playNarration("sentiment_probabilities_note")}
                style={{ marginTop: 16, padding: 10, borderRadius: 12 }}
              >
                <Text style={{ color: C.muted, fontSize: 14, lineHeight: 22 }}>{copy.sentiment.probabilitiesNote}</Text>
              </AIExhibitNarrationPressable>

              <Pressable onPress={nextReview} style={({ pressed }) => ({
                marginTop: 18, minHeight: 52, alignSelf: "flex-start", paddingHorizontal: 18, paddingVertical: 12,
                borderRadius: 14, borderWidth: 1, borderColor: C.border, backgroundColor: pressed ? C.accentSoft : C.surface,
              })}>
                <Text style={{ color: C.text, fontSize: 16, fontWeight: "900" }}>{copy.sentiment.another} →</Text>
              </Pressable>
            </>
          )}
        </View>
      </View>

      <AIExhibitNarrationPressable
        audioEnabled={audioEnabled}
        onPress={() => playNarration("sentiment_broader_classification")}
        style={{ marginTop: 18, padding: 18, borderRadius: 16, borderWidth: 1, borderColor: C.border, backgroundColor: C.surfaceRaised }}
      >
        <Text style={{ color: C.text, fontSize: 16, lineHeight: 25 }}>{copy.sentiment.broader}</Text>
      </AIExhibitNarrationPressable>
    </View>
  );
}
